import PropTypes from "prop-types";
import React, { useRef } from "react";

import { useDeviceInfo, useResizeObserver } from "@swa-ui/browser";
import { Image, Link, Text } from "@swa-ui/core";
import { fireDestinationOffersByThemeAnalytics } from "@swa-ui/dotcom-analytics";
import i18n from "@swa-ui/locale";
import { applyOverrides } from "@swa-ui/placements";

import styles from "./DestinationByThemeCard.module.scss";

export const DestinationByThemeCard = (props) => {
  const { graphic, link, onImageLoad, station, stationDisplayName } = props;
  const imageRef = useRef(null);
  const { screenSize } = useDeviceInfo();

  useResizeObserver({ callback: onImageLoad, element: imageRef });

  return (
    <>
      {link && <Link {...getLinkProps()}>{renderGraphicImagePath()}</Link>}
      <Text {...getTextProps()}>{stationDisplayName}</Text>
    </>
  );

  function renderGraphicImagePath() {
    return (
      graphic.imagePath && (
        <div ref={imageRef}>
          <Image {...getImageProps()} />
        </div>
      )
    );
  }

  function getLinkProps() {
    const { newWindow, target } = applyOverrides(link, screenSize);

    return {
      "aria-label": i18n("DestinationByThemeCard__LINK_ARIA_LABEL", {
        stationName: stationDisplayName,
      }),
      className: styles.destinationByThemeLink,
      href: applyStation(target),
      newWindow,
      onClick: () => fireDestinationOffersByThemeAnalytics(station),
    };
  }

  function getTextProps() {
    return {
      bold: true,
      element: "p",
      size: "xlarge",
    };
  }

  function applyStation(item) {
    item = item.replace(/\${.*?}/, station);

    return item;
  }

  function getImageProps() {
    const { altText, height, imagePath, width } = graphic;

    return {
      alt: altText,
      className: styles.destinationByThemeImage,
      fitToContainer: true,
      height: height,
      src: applyStation(imagePath),
      width,
    };
  }
};

DestinationByThemeCard.displayName = "DestinationByThemeCard";
DestinationByThemeCard.propTypes = {
  /** Object that contains DestinationByOrigin card image props. */
  graphic: PropTypes.shape({
    altText: PropTypes.string.isRequired,
    imagePath: PropTypes.string,
    override: PropTypes.shape({
      large: PropTypes.shape({
        imagePath: PropTypes.string.isRequired,
      }),
      medium: PropTypes.shape({
        imagePath: PropTypes.string.isRequired,
      }),
      small: PropTypes.shape({
        imagePath: PropTypes.string.isRequired,
      }),
    }),
  }),

  /** Object that contains link props. */
  link: PropTypes.shape({
    newWindow: PropTypes.bool,
    override: PropTypes.shape({
      large: PropTypes.shape({
        target: PropTypes.string.isRequired,
      }),
      medium: PropTypes.shape({
        target: PropTypes.string.isRequired,
      }),
      small: PropTypes.shape({
        target: PropTypes.string.isRequired,
      }),
    }),
    target: PropTypes.string.isRequired,
  }),

  /** String for the airport station. */
  station: PropTypes.string.isRequired,

  /** String of destination station name. */
  stationDisplayName: PropTypes.string.isRequired,
};
